import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { forms } from '../services/api';
import { NmIsoDateField } from '../components/NmIsoDateField';

function ModalOverlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 3000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--fg-4)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '7px 10px', borderRadius: 'var(--r-2)',
  border: '1px solid var(--surface-sunken)', background: 'var(--surface-0)',
  color: 'var(--fg-1)', fontSize: 12, fontFamily: 'var(--font-body)',
  outline: 'none', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.08)',
};

const btnBase: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 'var(--r-2)', border: 'none',
  cursor: 'pointer', fontSize: 12, fontWeight: 700,
  fontFamily: 'var(--font-body)', transition: 'opacity 0.12s',
};

// ─── NoteFormModal ─────────────────────────────────────────────────────────────

interface NoteFormModalProps {
  date: string;
  userId: number;
  onSuccess: () => void;
  onClose: () => void;
}

export function NoteFormModal({ date, userId, onSuccess, onClose }: NoteFormModalProps) {
  const { t } = useTranslation();
  const [noteDate, setNoteDate] = useState(date);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { setError(t('noteForm.error.titleRequired')); return; }
    setError('');
    setSubmitting(true);
    try {
      await forms.createNote(userId, noteDate, title.trim(), description.trim());
      onSuccess();
    } catch {
      setError(t('noteForm.error.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-surface" style={{
        background: 'var(--surface-0)', borderRadius: 'var(--r-4)',
        boxShadow: 'none', width: 400, maxWidth: '95vw',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px 12px', borderBottom: '1px solid var(--surface-sunken)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--fg-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('noteForm.headerLabel', { date: noteDate })}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-1)', fontFamily: 'var(--font-display)', marginTop: 2 }}>
              {t('noteForm.title')}
            </div>
          </div>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-4)', fontSize: 20, lineHeight: 1, padding: 4 }}
          >{t('common.close')}</button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

            <Field label={t('common.date', 'Date')}>
              <NmIsoDateField value={noteDate} onChange={setNoteDate} required sheetZIndex={3100} />
            </Field>

            <Field label={t('noteForm.field.title')}>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t('noteForm.field.titlePlaceholder')}
                required
                autoFocus
                style={inputStyle}
              />
            </Field>

            <Field label={t('noteForm.field.content')}>
              <textarea
                rows={5}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={t('noteForm.field.contentPlaceholder')}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              />
            </Field>

            {error && (
              <div style={{ color: '#dc2626', fontSize: 11, fontWeight: 600, padding: '6px 10px', background: '#fee2e2', borderRadius: 6 }}>
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '12px 20px', borderTop: '1px solid var(--surface-sunken)',
            display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
          }}>
            <button type="button" onClick={onClose}
              style={{ ...btnBase, background: 'var(--surface-1)', color: 'var(--fg-2)' }}>
              {t('noteForm.action.cancel')}
            </button>
            <button type="submit" disabled={submitting}
              style={{ ...btnBase, background: 'var(--color-accent)', color: '#fff', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? t('noteForm.action.saving') : t('noteForm.action.save')}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}
